import * as React from "react";
import { Search, X } from "lucide-react";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";

export function FilterBar({
  className,
  variant = "boxed",
  children,
}: {
  className?: string;
  variant?: "boxed" | "flat";
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        // Phones (Figma mobile frames): a two-column grid — the first item
        // (search) spans the row, selects fill their cells, and an action
        // link spans the row. From sm up it is the original wrapping row.
        // Expressed as `flex` + `max-sm:grid` rather than the prototype's
        // `grid` + `sm:flex` so the base `flex`/`flex-wrap` tokens the
        // filter-bar pins assert on stay present; Tailwind emits the
        // `max-sm:` media block after those base rules, so the grid wins
        // below 640px and the row wins above it — same rendering either way.
        "mb-4 flex flex-wrap items-center gap-2",
        "max-sm:grid max-sm:grid-cols-2 max-sm:[&>*:first-child]:col-span-2 max-sm:[&_select]:w-full max-sm:[&>a]:col-span-2 max-sm:[&>a]:w-full",
        variant === "boxed" && "rounded-lg border border-line bg-surface px-3.5 py-2.5 max-sm:rounded-2xl max-sm:p-3",
        className
      )}
    >
      {children}
    </div>
  );
}

export function FilterSearch({
  value,
  onChange,
  placeholder,
  ariaLabel,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /**
   * Accessible name for the field. A placeholder is not an accessible name —
   * assistive technology and the accessibility tree expose it as an empty
   * name — so a search box without this is announced as an unlabelled
   * "edit text" control (ALL-5643 edge-case audit, found on /wallets).
   */
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <div className={cn("relative flex-1 min-w-[200px] max-w-[300px]", className)}>
      <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        data-page-search=""
        aria-keyshortcuts="/"
        className="w-full py-[7px] pl-9 pr-2 text-sm text-ink outline-none placeholder:text-subtle"
      />
    </div>
  );
}

export function FilterSelect({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  ariaLabel?: string;
}) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} aria-label={ariaLabel}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  );
}
export interface FilterChip {
  key: string;
  label: string;
  clear: () => void;
}

/**
 * Active filters as removable chips plus "Clear all" (ALL-5793, ALL-5809). Renders nothing when no
 * filter is active, so a list without filters keeps its layout.
 */
export function FilterChips({
  chips,
  onClearAll,
  className,
  testId = "filter-chips",
}: {
  chips: FilterChip[];
  onClearAll: () => void;
  className?: string;
  testId?: string;
}) {
  if (chips.length === 0) return null;
  return (
    <div className={cn("mb-4 flex flex-wrap items-center gap-2", className)} data-testid={testId}>
      {chips.map((c) => (
        <span key={c.key} className="inline-flex items-center gap-1 rounded-full border border-line bg-surface py-0.5 pl-2.5 pr-1 text-xs font-medium text-text">
          {c.label}
          <button
            type="button"
            onClick={c.clear}
            aria-label={`Remove filter ${c.label}`}
            className="grid size-5 place-items-center rounded-full text-muted hover:bg-bg hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
          >
            <X className="size-3" aria-hidden />
          </button>
        </span>
      ))}
      <button type="button" onClick={onClearAll} className="text-xs font-semibold text-primary hover:underline">
        Clear all
      </button>
    </div>
  );
}

/** True when every whitespace-separated term of `query` appears in one of `fields` (case-insensitive). */
export function matchesAllTerms(query: string, fields: (string | null | undefined)[]): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const hay = fields.filter(Boolean).join(" ").toLowerCase();
  return terms.every((t) => hay.includes(t));
}
